import { questionBoxVideoMentionKey } from "~/modules/shared/gvtui/organisms/QuestionBox/QuestionBoxVideoMention";
import type { QuestionContextVideo } from "~/modules/shared/gvtui/organisms/QuestionBox/QuestionContextVideos";
import { mentionClipDurationSec } from "../utils/videoMentionPlayback";
import {
	artifactVideoSourceMentions,
	type VideoSourceMention,
} from "../utils/videoSourceMentions";
import type { ChatPanelUiState, SpeakingBlockState } from "./chatPanelState";

const toQuestionContextVideo = (
	mention: VideoSourceMention,
	fallbackTitle: string,
): QuestionContextVideo => {
	const handle =
		mention.social_account?.handle?.trim() ||
		mention.influencer?.handle?.trim() ||
		null;
	return {
		id: mention.video_id ?? mention.id,
		title: mention.title?.trim() || fallbackTitle,
		thumbnailUrl: mention.thumbnail_url,
		durationSec: mentionClipDurationSec(mention),
		handle,
		startTimeSec: mention.start_time_sec,
		endTimeSec: mention.end_time_sec,
	};
};

/**
 * Every clip a speaking block puts in front of the user, in the order it does:
 * the videos cited in its text, and the sources behind any `video_sources`
 * artifact it carries. Both live in the block's mentions, whose placeholders in
 * the markdown are what put them in order.
 */
const blockContextVideos = (
	block: SpeakingBlockState,
): QuestionContextVideo[] =>
	Object.entries(block.mentions)
		.sort(
			([placeholderA], [placeholderB]) =>
				block.markdown.indexOf(placeholderA) -
				block.markdown.indexOf(placeholderB),
		)
		.flatMap(([, entry]) => {
			if (entry.kind === "video") {
				return [
					toQuestionContextVideo(entry.data, `Video ${entry.data.index}`),
				];
			}
			if (entry.kind === "artifact") {
				return artifactVideoSourceMentions(entry.data).map((source) =>
					toQuestionContextVideo(source, "Untitled video"),
				);
			}
			return [];
		});

/** Unique mentioned clips in conversation order, keyed by video id and range. */
export const conversationContextVideos = (
	state: Pick<ChatPanelUiState, "interactions">,
): QuestionContextVideo[] => {
	const seen = new Set<string>();
	const videos: QuestionContextVideo[] = [];
	for (const interaction of state.interactions) {
		for (const responseBlock of interaction.answer?.responseBlocks ?? []) {
			if (responseBlock.kind !== "speaking") {
				continue;
			}
			for (const video of blockContextVideos(responseBlock.block)) {
				const key = questionBoxVideoMentionKey(video);
				if (seen.has(key)) {
					continue;
				}
				seen.add(key);
				videos.push(video);
			}
		}
	}
	return videos;
};
